import React, { useEffect } from "react";
import { useTemplateAnalytics } from "../hooks/useTemplateAnalytics";
import "./TemplateAnalyticsModal.css";

const StatusBadge = ({ status }) => {
  const classMap = {
    sent: "badge-sent",
    delivered: "badge-delivered",
    read: "badge-read",
    error: "badge-error",
    pending: "badge-pending",
    processing: "badge-pending",
    queued: "badge-pending",
  };
  return (
    <span className={`analytics-status-badge ${classMap[status] || "badge-pending"}`}>
      {status}
    </span>
  );
};

const TemplateAnalyticsModal = ({ template, isOpen, onClose }) => {
  const {
    analyticsData,
    loading,
    exporting,
    currentPage,
    setCurrentPage,
    statusFilter,
    setStatusFilter,
    dateRange,
    setDateRange,
    fetchAnalytics,
    exportAnalytics,
    resetState,
  } = useTemplateAnalytics();

  useEffect(() => {
    if (isOpen && template?._id) {
      fetchAnalytics(template._id);
    }
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, template?._id]);

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchAnalytics(template._id, {
      page: 1,
      status: statusFilter || undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  const handleStatusFilterChange = (e) => {
    const newStatus = e.target.value;
    setStatusFilter(newStatus);
    setCurrentPage(1);
    fetchAnalytics(template._id, {
      page: 1,
      status: newStatus || undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAnalytics(template._id, {
      page,
      status: statusFilter || undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  const handleClose = () => {
    onClose();
  };

  const handleExportJson = () => {
    exportAnalytics(template._id, template.name, {
      status: statusFilter || undefined,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  if (!template) return null;

  const summary = analyticsData?.summary;
  const records = analyticsData?.records;
  const pagination = records?.pagination;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div
      className={`template-modal-overlay ${isOpen ? "visible" : ""}`}
      onClick={handleClose}
    >
      <div
        className="template-modal-container analytics-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="template-modal-header">
          <h3 className="template-modal-title">
            Analytics: {template.name}
          </h3>
          <div className="analytics-header-actions">
            <button
              className="bouncy-button small"
              onClick={handleExportJson}
              disabled={exporting || loading}
              title="Export all records as JSON"
            >
              {exporting ? (
                <>
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Exporting...</span>
                  </div>
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-download"></i>
                  <span>Export JSON</span>
                </>
              )}
            </button>
            <button className="template-modal-close" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="template-modal-body">
          {/* Date Range Picker */}
          <div className="analytics-date-range">
            <div className="analytics-date-field">
              <label>From</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
                }
              />
            </div>
            <div className="analytics-date-field">
              <label>To</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) =>
                  setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
                }
              />
            </div>
            <button
              className="bouncy-button small"
              onClick={handleApplyFilters}
              disabled={loading}
            >
              <span>Apply</span>
            </button>
          </div>

          {loading && !analyticsData ? (
            <div className="text-center" style={{ padding: "40px 0" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              {summary && (
                <div className="analytics-summary-cards">
                  <div className="analytics-card analytics-card-total">
                    <div className="analytics-card-value">{summary.total}</div>
                    <div className="analytics-card-label">Total</div>
                  </div>
                  <div className="analytics-card analytics-card-sent">
                    <div className="analytics-card-value">{summary.sent}</div>
                    <div className="analytics-card-label">Sent</div>
                  </div>
                  <div className="analytics-card analytics-card-delivered">
                    <div className="analytics-card-value">{summary.delivered}</div>
                    <div className="analytics-card-label">Delivered</div>
                  </div>
                  <div className="analytics-card analytics-card-read">
                    <div className="analytics-card-value">{summary.read}</div>
                    <div className="analytics-card-label">Read</div>
                  </div>
                  <div className="analytics-card analytics-card-error">
                    <div className="analytics-card-value">{summary.error}</div>
                    <div className="analytics-card-label">Failed</div>
                  </div>
                </div>
              )}

              {/* Status Filter */}
              <div className="analytics-filter-row">
                <label>Filter by status:</label>
                <select value={statusFilter} onChange={handleStatusFilterChange}>
                  <option value="">All</option>
                  <option value="sent">Sent</option>
                  <option value="delivered">Delivered</option>
                  <option value="read">Read</option>
                  <option value="error">Error</option>
                </select>
                {loading && (
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                )}
              </div>

              {/* Records Table */}
              <div className="analytics-table-wrapper">
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Phone / ID</th>
                      <th>Status</th>
                      <th>Sent At</th>
                      <th>Updated At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records?.data?.length > 0 ? (
                      records.data.map((record) => (
                        <tr key={record._id}>
                          <td>{record.contact?.name || "-"}</td>
                          <td className="analytics-phone">
                            {record.contact?.phone || record.platformReferenceId || "-"}
                          </td>
                          <td>
                            <StatusBadge status={record.status} />
                          </td>
                          <td>{formatDate(record.createdAt)}</td>
                          <td>{formatDate(record.updatedAt)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center" style={{ padding: "24px" }}>
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="analytics-pagination">
                  <button
                    className="bouncy-button white small"
                    disabled={currentPage <= 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <span>Prev</span>
                  </button>
                  <span className="analytics-page-info">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    className="bouncy-button white small"
                    disabled={currentPage >= pagination.totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <span>Next</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="template-modal-footer">
          <button className="bouncy-button" onClick={handleClose}>
            <span>Close</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateAnalyticsModal;
